import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Store, Truck, BarChart3, Bell,
  LogOut, Home, ChevronRight, Map, Megaphone, Scale, Shield, Activity, History,
} from "lucide-react";

// Import admin pages
import AdminUtilisateurs from "./admin/Utilisateurs";
import AdminBoutiques from "./admin/Boutiques";
import AdminLivraisons from "./admin/Livraisons";
import AdminStatistiques from "./admin/Statistiques";
import AdminCarte from "./admin/Carte";
import AdminPublicites from "./admin/Publicites";
import AdminDisputes from "./admin/Disputes";
import AuditLogsPanel from "./admin/AuditLogsPanel";
import SubscriptionsPanel from "./admin/SubscriptionsPanel";
import WaveTopupsPanel from "./admin/WaveTopupsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel() {
  const navigate = useNavigate();

  // Auth admin basée sur JWT Bearer signé côté serveur (2h de validité).
  // Le token est stocké en sessionStorage + rattaché via axios interceptor à chaque requête admin.
  const ADMIN_SESSION_TTL_MS = 2 * 60 * 60 * 1000; // 2h

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const raw = sessionStorage.getItem('admin_session');
      if (!raw) return false;
      const { token, ts, expires_in } = JSON.parse(raw);
      const ttl = (expires_in ? expires_in * 1000 : ADMIN_SESSION_TTL_MS);
      if (token && typeof ts === 'number' && Date.now() - ts < ttl) {
        return true;
      }
      sessionStorage.removeItem('admin_session');
      return false;
    } catch {
      sessionStorage.removeItem('admin_session');
      return false;
    }
  });
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("statistiques");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications?unread_only=true&limit=100`);
      setUnreadNotifications(response.data.length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotificationCount();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success && response.data.access_token) {
        setIsAuthenticated(true);
        sessionStorage.setItem(
          'admin_session',
          JSON.stringify({
            token: response.data.access_token,
            ts: Date.now(),
            expires_in: response.data.expires_in || 7200,
          })
        );
        toast.success("Accès administrateur accordé");
        fetchNotificationCount();
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Mot de passe incorrect");
      } else {
        toast.error("Erreur de connexion au serveur");
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_session');
    navigate('/');
  };

  const menuItems = [
    { id: "cockpit", label: "Cockpit Live", icon: Activity, external: "/admin/cockpit" },
    { id: "statistiques", label: "Statistiques", icon: BarChart3 },
    { id: "livraisons", label: "Livraisons", icon: Truck },
    { id: "utilisateurs", label: "Utilisateurs", icon: Users },
    { id: "boutiques", label: "Boutiques", icon: Store },
    { id: "carte", label: "Carte GPS", icon: Map },
    { id: "publicites", label: "Publicités", icon: Megaphone },
    { id: "disputes", label: "Litiges", icon: Scale },
    { id: "subscriptions", label: "Abonnements", icon: Store },
    { id: "wave", label: "Recharges Wave", icon: History },
    { id: "audit", label: "Journal d'audit", icon: History },
  ];

  // ============================
  //  LOGIN — Luxury Palette
  // ============================
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 relative overflow-hidden">
        {/* halos décoratifs */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#003594]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="w-full max-w-md bg-[#1E293B] border border-white/5 rounded-[2.5rem] p-10 shadow-2xl relative z-10">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-2xl flex items-center justify-center font-black text-2xl text-[#0F172A] mb-6 shadow-lg shadow-[#D4AF37]/30">
            A
          </div>
          <h1
            className="text-2xl font-black text-white text-center tracking-tight"
            data-testid="admin-login-title"
          >
            Accès <span className="text-[#D4AF37] italic">Administrateur</span>
          </h1>
          <p className="text-[10px] text-slate-400 text-center mt-2 uppercase tracking-widest font-bold">
            WÔMA de ARTEKNOS
          </p>

          <form onSubmit={handleLogin} className="space-y-5 mt-8">
            <div>
              <Label htmlFor="admin-password" className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                Mot de passe
              </Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
                className="mt-2 bg-[#0F172A] border-white/10 text-white placeholder:text-slate-600 focus:border-[#D4AF37] focus:ring-[#D4AF37] rounded-2xl py-6"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-6 font-black uppercase tracking-widest text-xs shadow-lg shadow-[#D4AF37]/20"
              data-testid="admin-login-btn"
            >
              Accéder au panneau
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ============================
  //  DASHBOARD — Luxury Palette
  // ============================
  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#1E293B] border-r border-white/5 transition-all duration-300 flex flex-col fixed h-full z-50`}
      >
        {/* Logo */}
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center font-black text-lg text-[#0F172A] flex-shrink-0 shadow-md shadow-[#D4AF37]/20">
              A
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-base font-black tracking-tighter text-white">ARTEKNOS</h1>
                <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">Administration</p>
              </div>
            )}
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 bg-[#0F172A] p-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/50 transition-colors"
          data-testid="sidebar-toggle"
        >
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Menu */}
        <nav className="flex-1 py-6 px-3 space-y-1.5 overflow-y-auto">
          {menuItems.map((item) => {
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.external) {
                    navigate(item.external);
                  } else {
                    setActiveTab(item.id);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-sm font-bold transition-all ${
                  active
                    ? 'bg-[#003594] text-white shadow-lg shadow-[#003594]/30'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#D4AF37]' : ''}`} />
                {sidebarOpen && <span className="tracking-tight">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom badge + actions */}
        <div className="p-3 border-t border-white/5 space-y-2">
          {sidebarOpen && (
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-2xl p-3 mb-2">
              <p className="text-[9px] font-black text-[#D4AF37] uppercase tracking-widest flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Mode Administrateur
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Accès Certifié · ID-126</p>
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2 text-slate-400 hover:bg-white/5 hover:text-white rounded-xl transition-all text-sm font-bold"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Accueil</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl transition-all text-sm font-bold"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="bg-[#1E293B]/70 backdrop-blur-xl border-b border-white/5 px-6 md:px-10 py-5 sticky top-0 z-40">
          <div className="flex justify-between items-center gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-white capitalize">
                {menuItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
              </h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">
                Bienvenue, Super Admin ARTEKNOS
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="relative p-2.5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl transition-colors"
                data-testid="notifications-btn"
              >
                <Bell className="w-4 h-4 text-slate-300" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
              <div className="text-right">
                <p className="text-sm font-bold text-white">Admin</p>
                <p className="text-[9px] text-slate-500 uppercase tracking-widest font-bold">giearteknos@gmail.com</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 md:p-10">
          {activeTab === "statistiques" && <AdminStatistiques />}
          {activeTab === "livraisons" && <AdminLivraisons />}
          {activeTab === "utilisateurs" && <AdminUtilisateurs onRefresh={fetchNotificationCount} />}
          {activeTab === "boutiques" && <AdminBoutiques />}
          {activeTab === "carte" && <AdminCarte />}
          {activeTab === "publicites" && <AdminPublicites />}
          {activeTab === "disputes" && <AdminDisputes />}
          {activeTab === "subscriptions" && <SubscriptionsPanel />}
          {activeTab === "wave" && <WaveTopupsPanel />}
          {activeTab === "audit" && <AuditLogsPanel />}
        </div>
      </main>
    </div>
  );
}
